import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { SITE, waLink, localBusinessSchema, breadcrumbSchema } from "@/data/site";
import { PageHero, Section } from "@/components/sections";
import { Reveal } from "@/components/motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MessageCircle, Phone, Mail, MapPin, CheckCircle2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", business: "", org_type: "", whatsapp: "", email: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.org_type || !form.whatsapp || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Thank you! We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact Sankofa Launch Studio — Book a Free Website Briefing in Ghana"
        description="Book a free briefing this week. Contact Sankofa Launch Studio by WhatsApp, phone or email, or send us a message and we'll get your project into the next sprint."
        jsonLd={[
          localBusinessSchema(),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]),
        ]}
      />
      <PageHero
        eyebrow="Contact"
        breadcrumb="Home / Contact"
        title="Let's build yours"
        intro="Book a free briefing this week and we'll get your project into the next sprint."
      />

      <Section className="bg-cream">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          {/* Details */}
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-3xl font-600 text-navy">Talk to us directly</h2>
              <p className="mt-3 text-ink">Prefer to chat? Reach us any way that suits you.</p>
              <ul className="mt-8 space-y-4">
                <li>
                  <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-4 rounded-2xl bg-white p-5 shadow-soft transition-transform hover:-translate-y-0.5">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white"><MessageCircle className="h-6 w-6" /></span>
                    <span><span className="block text-xs uppercase tracking-wide text-mutedink">WhatsApp</span><span className="font-600 text-navy">{SITE.whatsappDisplay}</span></span>
                  </a>
                </li>
                <li>
                  <a href={`tel:${SITE.phoneDisplay.replace(/\s+/g, "")}`} className="flex items-center gap-4 rounded-2xl bg-white p-5 shadow-soft transition-transform hover:-translate-y-0.5">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-white"><Phone className="h-6 w-6" /></span>
                    <span><span className="block text-xs uppercase tracking-wide text-mutedink">Call</span><span className="font-600 text-navy">{SITE.phoneDisplay}</span></span>
                  </a>
                </li>
                <li>
                  <a href={`mailto:${SITE.email}`} className="flex items-center gap-4 rounded-2xl bg-white p-5 shadow-soft transition-transform hover:-translate-y-0.5">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-white"><Mail className="h-6 w-6" /></span>
                    <span><span className="block text-xs uppercase tracking-wide text-mutedink">Email</span><span className="font-600 text-navy">{SITE.email}</span></span>
                  </a>
                </li>
                <li className="flex items-center gap-4 rounded-2xl bg-white p-5 shadow-soft">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-white"><MapPin className="h-6 w-6" /></span>
                  <span><span className="block text-xs uppercase tracking-wide text-mutedink">Location</span><span className="font-600 text-navy">{SITE.addressLine}</span></span>
                </li>
              </ul>

              <div className="mt-6 overflow-hidden rounded-2xl border border-navy/10 shadow-soft" data-testid="map-placeholder">
                <iframe
                  title="Sankofa Launch Studio location — Accra, Ghana"
                  src="https://www.google.com/maps?q=Accra%2C%20Ghana&output=embed"
                  className="h-64 w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.08}>
              <div className="rounded-2xl bg-white p-7 shadow-softlg md:p-10">
                {done ? (
                  <div className="flex flex-col items-center py-14 text-center" data-testid="contact-success">
                    <CheckCircle2 className="h-16 w-16 text-gold" />
                    <h3 className="mt-5 font-serif text-3xl font-600 text-navy">Akwaaba — thank you!</h3>
                    <p className="mt-3 max-w-md text-ink">
                      Your message has reached us. We'll be in touch shortly to arrange your free briefing. For anything
                      urgent, message us on WhatsApp.
                    </p>
                    <a href={waLink()} target="_blank" rel="noopener noreferrer" className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-600 text-navy-dark transition-transform hover:-translate-y-0.5">
                      <MessageCircle className="h-4 w-4" /> Message us now
                    </a>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="name" className="text-navy">Name *</Label>
                        <Input id="name" data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Your full name" className="mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="business" className="text-navy">Business name</Label>
                        <Input id="business" data-testid="contact-business" value={form.business} onChange={set("business")} placeholder="Your organisation" className="mt-2" />
                      </div>
                    </div>

                    <div>
                      <Label className="text-navy">Type of organisation *</Label>
                      <Select value={form.org_type} onValueChange={set("org_type")}>
                        <SelectTrigger data-testid="contact-orgtype" className="mt-2">
                          <SelectValue placeholder="Select one" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="School">School</SelectItem>
                          <SelectItem value="Hotel">Hotel</SelectItem>
                          <SelectItem value="Shop">Shop</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="whatsapp" className="text-navy">WhatsApp number *</Label>
                        <Input id="whatsapp" data-testid="contact-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} placeholder="+233 ..." className="mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-navy">Email *</Label>
                        <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="mt-2" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-navy">Message *</Label>
                      <Textarea id="message" data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="Tell us about your project, goal and budget." rows={5} className="mt-2" />
                    </div>

                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="contact-submit-button"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-sans font-600 text-navy-dark transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                    >
                      {sending ? "Sending..." : <>Book a free briefing <Send className="h-4 w-4" /></>}
                    </button>
                    <p className="text-center text-xs text-mutedink">
                      We'll only use your details to respond to your enquiry.
                    </p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </Section>
    </>
  );
}
